import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api
      .get(`/blog/${slug}`)
      .then((r) => { if (!cancel) setPost(r.data); })
      .catch(() => { if (!cancel) setPost(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="blog-detail-loading" className="bg-navy pt-44 pb-24 max-w-3xl mx-auto px-6 text-[#8A99AA] font-light">
        Loading…
      </div>
    );
  }
  if (!post) {
    return (
      <div className="bg-navy pt-44 pb-24 max-w-3xl mx-auto px-6 text-center">
        <h1 className="text-4xl text-[#F5F4F0] font-serif">Post not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-[#C6A87C]">
          <ArrowLeft size={14} /> Back to insights
        </Link>
      </div>
    );
  }

  return (
    <article data-testid={`blog-post-${post.slug}`} className="bg-navy pt-40 pb-28">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <Reveal y={14}>
          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 label-overline">
            <ArrowLeft size={14} /> All insights
          </Link>
        </Reveal>

        <header className="mt-10">
          <Reveal>
            <div className="flex items-center gap-3 text-[11px] text-[#8A99AA] uppercase tracking-[0.18em]">
              <span>{fmtDate(post.published_at)}</span>
              <span className="w-1 h-1 bg-[#C6A87C] rounded-full" />
              <span>{post.read_time}</span>
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl text-[#F5F4F0] leading-[1.05] font-serif tracking-tight">
              {post.title}
            </h1>
            <p className="mt-6 text-xl text-[#8A99AA] font-light leading-snug">{post.excerpt}</p>
            <div className="mt-6 text-sm text-[#F5F4F0]/80">
              By <span className="font-semibold text-[#F5F4F0]">{post.author}</span>
            </div>
          </Reveal>
        </header>

        <Reveal delay={0.15}>
          <div className="mt-12 aspect-[16/9] overflow-hidden clip-frame border border-[#C6A87C]/25">
            <img src={post.cover} alt="" className="w-full h-full object-cover" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 space-y-7 text-lg text-[#B8C2CE] font-light leading-relaxed">
            {post.content?.map((item, i) => {
              if (item && typeof item === "object" && item.h) {
                return (
                  <h2
                    key={i}
                    data-testid={`blog-heading-${i}`}
                    className="font-serif text-3xl text-[#F5F4F0] leading-tight pt-4"
                  >
                    {item.h}
                  </h2>
                );
              }
              return (
                <p key={i} data-testid={`blog-para-${i}`}>{item}</p>
              );
            })}
          </div>
        </Reveal>

        <div className="mt-16 border-t border-[#C6A87C]/15 pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#F5F4F0]/80 hover:text-[#C6A87C] transition-colors duration-300">
            <ArrowLeft size={14} /> More insights
          </Link>
          <Link to="/enroll" data-testid="blog-cta-enroll" className="inline-flex items-center gap-2 text-[#0F9D6B] hover:text-[#14B981] transition-colors duration-300">
            Request enrolment <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
}
